import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import userService from '../../app/services/user.service';
import useMathjax from '../../hooks/useMathjax';
import './PrintView.css';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/* ── helpers ── */
const fixImgSrc = (html) => {
  if (!html) return '';
  return html
    .replace(/src="ckfinder\//gi, 'src="https://quizmaster.motion.ac.in/ckfinder/')
    .replace(/src="\/ckfinder\//gi, 'src="https://quizmaster.motion.ac.in/ckfinder/');
};

const splitOptions = (raw) => {
  if (!raw) return [];
  const parts = raw.includes('@@@') ? raw.split('@@@') : raw.split('@');
  return [...new Set(parts.filter(Boolean))];
};

const hasContent = (html) =>
  html &&
  html.trim() !== '' &&
  !html.includes('<body></body>') &&
  html.trim() !== '<p></p>';

/* ── single question row ── */
const QuestionRow = ({ data, index, bilingual = false }) => {
  const engOptions = splitOptions(data.eng_option);
  const hindiOptions = splitOptions(data.h_option);
  const showHindi = bilingual && (hasContent(data.q_text_hindi) || hindiOptions.length > 0);

  return (
    <div className="pv-question-block">

      {/* META LINE 1: Q.No | Qid | Subject | Type | Toughness */}
      <div className="pv-meta-row1">
        <span>Q. No: {data.qno ?? index + 1}</span>
        <span>Qid: {data.qid}</span>
        {data.subject && <span>{data.subject}</span>}
        {data.qus_type && <span>{data.qus_type}</span>}
        {data.toughness && <span>{data.toughness}</span>}
      </div>

      {/* META LINE 2: Topic | Sub-topic */}
      {(data.topic_name || data.sub_topic_name) && (
        <div className="pv-meta-row2">
          {data.topic_name && <span>{data.topic_name}</span>}
          {data.sub_topic_name && <span>{data.sub_topic_name}</span>}
        </div>
      )}

      {/* QUESTION BODY — side by side if Hindi exists */}
      <div className={`pv-body ${showHindi ? 'pv-two-col' : ''}`}>
        <div className="pv-col">
          <div className="pv-qtext"
            dangerouslySetInnerHTML={{ __html: fixImgSrc(data.q_text) }}
          />
          {engOptions.length > 0 && (
            <ol className="pv-options">
              {engOptions.map((opt, i) => (
                <li key={i}><div dangerouslySetInnerHTML={{ __html: fixImgSrc(opt) }} /></li>
              ))}
            </ol>
          )}
        </div>

        {showHindi && (
          <div className="pv-col pv-col-hindi">
            <div className="pv-qtext"
              dangerouslySetInnerHTML={{ __html: fixImgSrc(data.q_text_hindi) }}
            />
            {hindiOptions.length > 0 && (
              <ol className="pv-options">
                {hindiOptions.map((opt, i) => (
                  <li key={i}><div dangerouslySetInnerHTML={{ __html: fixImgSrc(opt) }} /></li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      {/* ANSWER BAR */}
      <div className="pv-answer-bar">
        Answer:&nbsp;<span>{data.answer}</span>
      </div>

      {/* SOLUTION */}
      {(hasContent(data.sol_text) || (bilingual && hasContent(data.sol_text_hindi))) && (
        <div className={`pv-solution ${bilingual ? 'pv-two-col' : ''}`}>
          <div className="pv-col">
            {hasContent(data.sol_text)
              ? <div dangerouslySetInnerHTML={{ __html: fixImgSrc(data.sol_text) }} />
              : <span className="pv-na">English Solution Not Available</span>}
          </div>
          {bilingual && <div className="pv-col pv-col-hindi">
            {hasContent(data.sol_text_hindi)
              ? <div dangerouslySetInnerHTML={{ __html: fixImgSrc(data.sol_text_hindi) }} />
              : <span className="pv-na">Hindi Solution Not Available</span>}
          </div>}
        </div>
      )}

      <hr className="pv-separator" />
    </div>
  );
};

/* ── main component ── */
const PrintView = () => {
  const query = useQuery();
  const uid = query.get('uid');
  const sid = query.get('sid');
  const pid = query.get('pid');
  // Print bilingual only when explicitly requested. Do not infer from content presence.
  const bilingual = query.get('lang') === 'both';
  const [qData, setQData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faculty_name, setFacultyName] = useState('');

  useMathjax(qData);

  useEffect(() => {
    if (!uid || !sid || !pid) return;
    userService.getViewPaper({ uid, sid, pid }).then((data) => {
      setQData(data.data);
      setLoading(false);
    });
  }, [uid, sid, pid]);

  useEffect(() => {
    if (qData) {
      setTimeout(() => window.print(), 800);
    }
  }, [qData]);

  if (!uid || !sid || !pid)
    return <div className="text-center mt-5"><h4>Missing Required Parameters</h4></div>;
  if (loading || !qData)
    return <div className="text-center mt-5">Loading...</div>;

  const questions = qData.paper_matrix ? Object.values(qData.paper_matrix) : [];
  const info = qData.paper_info || {};
  console.log(info);

  return (
    <div className="pv-root printable">

      {/* ── PAPER INFO PANEL ── */}
      <div className="pv-panel pv-panel-info">
        
        <div className="pv-panel-header">
          <span>{info.paper_name || 'Paper Details'}</span>
        </div>
        <div className="pv-panel-body">
          {info.batch_name   && <div><b>Batch:</b> {info.batch_name}</div>}
          {info.Session      && <div><b>Session:</b> {info.Session}</div>}
          {info.centers      && <div><b>Center:</b> {info.centers}</div>}
          {info.classes      && <div><b>Class:</b> {info.classes}</div>}
          {info.target       && <div><b>Target:</b> {info.target}</div>}
          {info.phase        && <div><b>Phase:</b> {info.phase}</div>}
          {info.creator      && <div><b>Paper Request by:</b> {info.creator}</div>}
          {info.to_be_held_on        && <div><b>Exam On:</b> {info.to_be_held_on}</div>}
          {questions[0].teacher        && <div><b>Paper Created By:</b> {questions[0].teacher }</div>}
          {info.status        && <div><b>Status:</b> {info.status==1 ? 'Completed' : 'Pending'}</div>}
          {info.to_be_completed_by   && <div><b>Last Date of Submission:</b> {info.to_be_completed_by}</div>}
          {info.to_be_final_by       && <div><b>Finalization Date:</b> {info.to_be_final_by}</div>}
          {info.paper_request_date       && <div><b>Paper Request On :</b> {info.to_be_held_on}</div>}
          {info.paper_format && <div><b>Format:</b> {info.paper_format}</div>}
          {info.topic_names  && <div><b>Syllabus:</b> {info.topic_names}</div>}
          
        </div>
      </div>

      {/* ── QUESTIONS PANEL ── */}
      <div className="pv-panel pv-panel-questions">
        <div className="pv-panel-header" style={{ backgroundColor: '#8b0000' }}>
          <span>Questions</span>
        </div>
        <div className="pv-panel-body pv-questions">
          {questions.map((data, i) => (
            <QuestionRow key={data.qid || i} data={data} index={i} bilingual={bilingual} />
          ))}
        </div>
      </div>

    </div>
  );
};

export default PrintView;
